import React from "react";
import { ArtistMetadata } from "../../../types/metadata";

// ─── Formatting helpers ───────────────────────────────────────────────────────

export const formatRegionLabel = (region: string): string => {
  const map: Record<string, string> = {
    east_coast: "East Coast",
    west_coast: "West Coast",
    south: "South",
    midwest: "Midwest",
    international: "International",
  };
  return (
    map[region] ??
    region.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

export const formatEraLabel = (era: string): string => {
  const map: Record<string, string> = {
    "80s": "'80s",
    "90s": "'90s",
    "2000s": "2000s",
    "2010s": "2010s",
    "2020s": "2020s",
  };
  return map[era] ?? era;
};

export const formatEraRange = (
  primary: string,
  secondary: string | null,
): string => {
  if (!secondary || secondary === primary) return formatEraLabel(primary);
  return `${formatEraLabel(primary)} → ${formatEraLabel(secondary)}`;
};

export const formatStyleLabel = (style: string): string => {
  const map: Record<string, string> = {
    lyrical: "Lyrical",
    conscious: "Conscious",
    street: "Street",
    mainstream: "Mainstream",
    underground: "Underground",
    experimental: "Experimental",
    melodic: "Melodic",
    trap: "Trap",
  };
  return (
    map[style] ??
    style.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

export const formatTagLabel = (tag: string): string =>
  tag
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bGoat\b/, "GOAT");

// ─── ArtistAttributeBar ───────────────────────────────────────────────────────

interface AttributeBarProps {
  label: string;
  value: number; // 1-10
  color?: string;
}

const ArtistAttributeBar: React.FC<AttributeBarProps> = ({
  label,
  value,
  color = "bg-blue-500",
}) => {
  const pct = Math.min(Math.max((value / 10) * 100, 0), 100);
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-gray-400">{label}</span>
        <span className="text-xs text-gray-500">{value}/10</span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ─── ArtistMetadataCard ───────────────────────────────────────────────────────

interface ArtistMetadataCardProps {
  metadata: ArtistMetadata | null | undefined;
}

const ArtistMetadataCard: React.FC<ArtistMetadataCardProps> = ({
  metadata,
}) => {
  if (!metadata) return null;

  const {
    primaryEra,
    secondaryEra,
    region,
    styles,
    mainstreamLevel,
    lyricismLevel,
    influenceLevel,
    tags,
  } = metadata;

  const hasEraOrRegion = primaryEra || region;
  const hasStyles = styles && styles.length > 0;
  const hasAttributes =
    lyricismLevel != null || mainstreamLevel != null || influenceLevel != null;
  const visibleTags = tags ? tags.slice(0, 6) : [];

  // Nothing meaningful to show
  if (
    !hasStyles &&
    !hasEraOrRegion &&
    !hasAttributes &&
    visibleTags.length === 0
  ) {
    return null;
  }

  return (
    <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
      <h3 className="text-xl font-bold text-white mb-5">Artist DNA</h3>

      {/* Styles */}
      {hasStyles && (
        <div className="mb-5">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Style
          </p>
          <div className="flex flex-wrap gap-2">
            {styles.slice(0, 3).map((style) => (
              <span
                key={style}
                className="px-3 py-1 text-sm font-medium rounded-full bg-purple-600/20 text-purple-300 border border-purple-500/30"
              >
                {formatStyleLabel(style)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Era + Region */}
      {hasEraOrRegion && (
        <div className="mb-5 flex flex-wrap gap-4">
          {primaryEra && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                Era
              </p>
              <span className="text-sm text-white font-medium">
                {formatEraRange(primaryEra, secondaryEra)}
              </span>
            </div>
          )}
          {region && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                Region
              </p>
              <span className="text-sm text-white font-medium">
                {formatRegionLabel(region)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Attribute bars */}
      {hasAttributes && (
        <div className="mb-5 space-y-3">
          {lyricismLevel != null && (
            <ArtistAttributeBar
              label="Lyricism"
              value={lyricismLevel}
              color="bg-blue-500"
            />
          )}
          {influenceLevel != null && (
            <ArtistAttributeBar
              label="Influence"
              value={influenceLevel}
              color="bg-yellow-500"
            />
          )}
          {mainstreamLevel != null && (
            <ArtistAttributeBar
              label="Mainstream"
              value={mainstreamLevel}
              color="bg-green-500"
            />
          )}
        </div>
      )}

      {/* Tags */}
      {visibleTags.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Tags
          </p>
          <div className="flex flex-wrap gap-2">
            {visibleTags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300 border border-gray-600"
              >
                {formatTagLabel(tag)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistMetadataCard;
